import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../services/apiClient';
import EmployeeUsageChart from './EmployeeUsageChart';

const UsageIndicator = () => {
  const { t } = useTranslation('common');
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiClient.get('/api/consultation-history/check-limit');
        setUsageData(data);
      } catch (err) {
        console.error('Error fetching usage data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsageData();
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-100 rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-300 rounded w-1/4 mb-4"></div>
          <div className="h-2 bg-gray-300 rounded w-full"></div>
        </div>
      </div>
    );
  }

  if (error || !usageData) {
    return (
      <div className="bg-gray-100 rounded-lg p-6">
        <div className="text-red-600 text-center">
          {t('statistics.usage.error', 'Unable to load usage information')}
        </div>
      </div>
    );
  }

  const { current, limit, remaining, percentage, subscriptionType } = usageData;
  const nextMonth = new Date();
  nextMonth.setMonth(nextMonth.getMonth() + 1, 1);
  const resetDate = nextMonth.toLocaleDateString('fr-FR', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  // Determine color scheme based on percentage
  const getColorScheme = (percentage) => {
    if (percentage >= 100) return 'red';
    if (percentage >= 90) return 'yellow';
    if (percentage >= 75) return 'orange';
    return 'green';
  };

  const colorScheme = getColorScheme(percentage);
  
  const colorClasses = {
    red: {
      bg: 'bg-red-500',
      text: 'text-red-700',
      border: 'border-red-200',
      icon: 'text-red-500'
    },
    yellow: {
      bg: 'bg-yellow-500',
      text: 'text-yellow-700',
      border: 'border-yellow-200',
      icon: 'text-yellow-500'
    },
    orange: {
      bg: 'bg-orange-500',
      text: 'text-orange-700',
      border: 'border-orange-200',
      icon: 'text-orange-500'
    },
    green: {
      bg: 'bg-green-500',
      text: 'text-green-700',
      border: 'border-green-200',
      icon: 'text-green-500'
    }
  };

  const colors = colorClasses[colorScheme];

  return (
    <div className="bg-gray-100 rounded-lg">
      <div className="px-6 py-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('statistics.usage.title', 'Utilisation mensuelle')}
        </h3>
      </div>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${colors.bg} bg-opacity-10`}>
              {percentage >= 100 ? (
                <ExclamationTriangleIcon className={`w-6 h-6 ${colors.icon}`} />
              ) : (
                <CheckCircleIcon className={`w-6 h-6 ${colors.icon}`} />
              )}
            </div>
            <div>
              <h4 className={`text-2xl font-bold ${colors.text}`}>
                {current} / {limit}
              </h4>
              <p className="text-sm text-gray-600">
                {t('statistics.usage.consultations', 'consultations')}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${colors.text}`}>
              {percentage}%
            </div>
            <p className="text-sm text-gray-600">
              {t('statistics.usage.used', 'utilisé')}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
          <div
            className={`h-3 rounded-full transition-all duration-300 ${colors.bg}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          ></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className={`p-3 rounded-lg border ${colors.border} bg-white`}>
            <div className="font-semibold text-gray-700">
              {t('statistics.usage.remaining', 'Restantes')}
            </div>
            <div className={`text-xl font-bold ${colors.text}`}>
              {remaining}
            </div>
          </div>
          
          <div className={`p-3 rounded-lg border ${colors.border} bg-white`}>
            <div className="font-semibold text-gray-700">
              {t('statistics.usage.subscription', 'Abonnement')}
            </div>
            <div className="text-lg font-bold text-gray-900 uppercase">
              {subscriptionType}
            </div>
          </div>
          
          <div className={`p-3 rounded-lg border ${colors.border} bg-white`}>
            <div className="font-semibold text-gray-700">
              {t('statistics.usage.resetDate', 'Remise à zéro')}
            </div>
            <div className="text-lg font-bold text-gray-900">
              {resetDate}
            </div>
          </div>
        </div>

        {percentage >= 100 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
              <p className="text-red-700 font-medium">
                {t('statistics.usage.limitReached', 'Limite mensuelle atteinte. Vous ne pouvez plus effectuer de consultations jusqu\'au 1er du mois prochain.')}
              </p>
            </div>
          </div>
        )}

        {percentage >= 90 && percentage < 100 && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 mr-2" />
              <p className="text-yellow-700 font-medium">
                {t('statistics.usage.approachingLimit', 'Attention: Vous approchez de votre limite mensuelle.')}
              </p>
            </div>
          </div>
        )}

        {/* Employee Usage Chart */}
        <EmployeeUsageChart />
      </div>
    </div>
  );
};

export default UsageIndicator;
